import React from "react";

import { HStack, Stack, Text } from "@/components/ui";
import { formatRecord } from "@/features/sports/formatters";
import { useTailwindBreakpoint } from "@/hooks";
import { cn } from "@/utils/cn";

import {
  GameStatus,
  HomeAwayLabel,
  LeagueBadge,
  ScoreLine,
  TeamAvatar,
  TeamScore,
  UpcomingTime,
} from "./primitives";

import type { Sport, Team } from "@/schemas";

const TeamBlock = ({
  team,
  side,
  isUpcoming,
}: {
  team: Team;
  side: "home" | "away";
  isUpcoming: boolean;
}) => {
  const breakpoint = useTailwindBreakpoint();
  const isMobile = breakpoint === "sm";
  const isHome = side === "home";

  return (
    <div
      className={`flex min-w-18 flex-col items-center gap-1 lg:flex-row lg:gap-3 ${isHome ? "lg:flex-row-reverse" : "lg:flex-row"} `}
    >
      <TeamAvatar team={team} />
      <Stack
        space={1}
        className={`min-w-0 items-center lg:w-16 lg:shrink-0 ${isHome ? "lg:items-end" : "lg:items-start"} `}
      >
        <Text
          size="md"
          weight="medium"
          truncate
          className={cn(`w-full text-center`, {
            "lg:text-right": isHome,
            "lg:text-left": !isHome,
          })}
        >
          {isMobile ? team.abbreviation : team.name}
        </Text>
        {isUpcoming ? (
          <>
            {/* mobile: home/away label, desktop: always record */}
            <HomeAwayLabel side={side} align="center" className="lg:hidden" />
            <Text
              size="xs"
              theme="muted"
              className="hidden whitespace-nowrap tabular-nums lg:block"
            >
              {formatRecord(team.record)}
            </Text>
          </>
        ) : (
          <Text
            size="xs"
            theme="muted"
            className={`whitespace-nowrap tabular-nums ${isHome ? "lg:text-right" : "lg:text-left"} w-full text-center`}
          >
            {formatRecord(team.record)}
          </Text>
        )}
      </Stack>
    </div>
  );
};

const Center = ({ event }: { event: Sport }) => {
  const isUpcoming = !event.data.is_live && !event.data.is_over;

  return (
    <Stack space={2} alignItems="center" justifyContent="center">
      {isUpcoming ? (
        <>
          <LeagueBadge league={event.league} />
          <UpcomingTime startAt={event.start_at} />
        </>
      ) : (
        <>
          {/* mobile: score lives in center */}
          <ScoreLine
            homeScore={event.data.home_team.score}
            awayScore={event.data.away_team.score}
            className="lg:hidden"
          />
          <GameStatus data={event.data} />
        </>
      )}
    </Stack>
  );
};

interface SecondaryScorecardProps {
  event: Sport;
}

const SecondaryScorecard = ({ event }: SecondaryScorecardProps) => {
  const isUpcoming = !event.data.is_live && !event.data.is_over;

  return (
    <div className="rounded-2xl border px-4 py-4 lg:px-6">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 lg:items-center lg:gap-4">
        <HStack alignItems="center" justifyContent="start" space={4}>
          <TeamBlock
            team={event.data.home_team}
            side="home"
            isUpcoming={isUpcoming}
          />
          {/* desktop only: score next to team */}
          {!isUpcoming && (
            <TeamScore
              value={event.data.home_team.score}
              align="right"
              className="hidden lg:block"
            />
          )}
        </HStack>

        <Center event={event} />

        <HStack alignItems="center" justifyContent="end" space={4}>
          {!isUpcoming && (
            <TeamScore
              value={event.data.away_team.score}
              align="left"
              className="hidden lg:block"
            />
          )}
          <TeamBlock
            team={event.data.away_team}
            side="away"
            isUpcoming={isUpcoming}
          />
        </HStack>
      </div>
    </div>
  );
};

export default SecondaryScorecard;
